import { JSX, PropsWithChildren } from 'react'
import styled from 'styled-components'

import { Button } from '../actions/Button'
import { ResponsiveButtonContainer } from '../global/CommonStyling'
import { BaseModal } from './BaseModal'
import { ModalContent } from './ModalContent'
import { ModalVisualMode } from './ModalTypes'
import { getIcon } from './ModalUtils'

export interface PromptModalProps
  extends Omit<ButtonsProps, 'mode'>, Omit<SecondaryButtonProps, 'mode'>, PrimaryButtonProps {
  contentLabel?: string
  onClose?: () => unknown
  isOpen: boolean
  header?: string
  headerAdditionalText?: string
  requestId?: string
  mode?: ModalVisualMode
  parentSelector?: Element | null
}

type PrimaryButtonProps = {
  isLoading?: boolean
  primaryButtonLabel: string
  onPrimaryButtonClick?: () => unknown
  loadingMode?: 'icon' | 'label'
  ref?: React.Ref<HTMLButtonElement>
}

const ButtonsContainer = styled(ResponsiveButtonContainer)<{ isPrimaryButtonLeft: boolean }>`
  flex-wrap: wrap-reverse;
  flex-direction: ${({ isPrimaryButtonLeft }) => (isPrimaryButtonLeft ? 'row-reverse' : 'row')};
`

const PrimaryButton = (props: PrimaryButtonProps) => (
  <Button
    loading={props.isLoading}
    label={props.primaryButtonLabel}
    onClick={props.onPrimaryButtonClick}
    disabled={!props.onPrimaryButtonClick}
    data-testid="prompt-modal-button"
    aria-disabled={props.isLoading}
    loadingMode={props.loadingMode}
    ref={props.ref}
  />
)

type SecondaryButtonProps = {
  onSecondaryButtonClick?: () => unknown
  secondaryButtonLabel: string
  mode: ModalVisualMode
  ref?: React.Ref<HTMLButtonElement>
}

const SecondaryButton = (props: SecondaryButtonProps) => (
  <Button
    disabled={!props.onSecondaryButtonClick}
    label={props.secondaryButtonLabel}
    mode={props.mode === ModalVisualMode.ExtraCautious ? 'danger' : undefined}
    onClick={props.onSecondaryButtonClick}
    variant="secondary"
    ref={props.ref}
  />
)

interface ButtonsProps extends PrimaryButtonProps, SecondaryButtonProps {
  mode: ModalVisualMode
}

export const buttonsContainerTestId = 'buttons-container'

export const ModalButtons = (props: ButtonsProps) => {
  const { mode } = props

  // Primary button placement:
  // - Different rows: always on top
  // - Same row: right by default, left in ExtraCautious or ThinkTwice mode
  const isPrimaryButtonLeft = [ModalVisualMode.ExtraCautious, ModalVisualMode.ThinkTwice].includes(mode)

  return (
    <ButtonsContainer
      data-testid={buttonsContainerTestId}
      $hasMarginTop={true}
      isPrimaryButtonLeft={isPrimaryButtonLeft}
    >
      <SecondaryButton {...props} />
      <PrimaryButton {...props} />
    </ButtonsContainer>
  )
}

export const PromptModal = (props: PropsWithChildren<PromptModalProps>): JSX.Element => {
  const {
    contentLabel,
    children,
    onClose,
    isOpen,
    header,
    headerAdditionalText,
    mode = ModalVisualMode.Plain,
    requestId,
    parentSelector,
  } = props

  const icon = getIcon(mode)

  return (
    <BaseModal
      parentSelector={parentSelector}
      contentLabel={contentLabel ?? header ?? 'Prompt modal'}
      onClose={onClose}
      isOpen={isOpen}
      footer={<ModalButtons {...props} mode={mode} />}
    >
      <ModalContent icon={icon} header={header} headerAdditionalText={headerAdditionalText} requestId={requestId}>
        {children}
      </ModalContent>
    </BaseModal>
  )
}
